import React, { useEffect } from 'react';

import type { FaseMontada } from './fases';
import PlacaVeiculo, { VeiculoDoTelao } from './PlacaVeiculo';
import { usePaginacaoPorAltura } from './usePaginacaoPorAltura';

/**
 * Uma fase do Telão: as esteiras empilhadas, cortadas em páginas pelo que cabe (at-130).
 *
 * Todas as colunas trocam de página JUNTAS: a página exibida é `paginaGlobal % páginas desta
 * coluna`. Coluna que cabe inteira fica parada e diz isso.
 */
type Item =
  | { tipo: 'cabecalho'; chave: string; nome: string; cor: string; total: number }
  | { tipo: 'placa'; chave: string; veiculo: VeiculoDoTelao }
  | { tipo: 'vazio'; chave: string };

interface ColunaDaFaseProps {
  fase: FaseMontada<VeiculoDoTelao>;
  paginaGlobal: number;
  onPaginas: (faseId: string, quantidade: number) => void;
  onAbrirVeiculo: (id: string) => void;
}

const ColunaDaFase: React.FC<ColunaDaFaseProps> = ({ fase, paginaGlobal, onPaginas, onAbrirVeiculo }) => {
  const itens: Item[] = [];
  fase.esteiras.forEach(({ esteira, carros }) => {
    itens.push({ tipo: 'cabecalho', chave: `c-${esteira.id}`, nome: esteira.nome, cor: esteira.cor, total: carros.length });
    if (carros.length === 0) itens.push({ tipo: 'vazio', chave: `v-${esteira.id}` });
    carros.forEach((v) => itens.push({ tipo: 'placa', chave: `p-${v.id}`, veiculo: v }));
  });

  // Muda quando entra/sai veículo ou muda o que a placa mostra (selo, trilha): remede.
  const assinatura = itens
    .map((it) => (it.tipo === 'placa' ? `${it.chave}:${it.veiculo.trilha?.length ?? 'x'}:${it.veiculo.horasParado ?? ''}` : it.chave))
    .join('|');
  const { corpoRef, medindo, paginas } = usePaginacaoPorAltura(assinatura);

  useEffect(() => {
    if (!medindo) onPaginas(fase.fase.id, paginas.length);
  }, [medindo, paginas.length, fase.fase.id, onPaginas]);

  const indice = paginas.length > 0 ? paginaGlobal % paginas.length : 0;
  const pagina = paginas[indice];
  const visiveis = new Set(medindo ? itens.map((_, i) => i) : pagina?.itens ?? []);
  const continuaDe = !medindo && pagina?.continuaDe != null ? itens[pagina.continuaDe] : null;

  const aviso = medindo ? '' : paginas.length > 1 ? `página ${indice + 1} de ${paginas.length}` : 'cabe inteira';

  return (
    <section className="telao-coluna" aria-label={`${fase.fase.nome}: ${fase.total} veículos`}>
      <header className="telao-coluna-cabecalho">
        <h2 className="telao-coluna-titulo">{fase.fase.nome}</h2>
        <span className="telao-coluna-meta">
          <small className={paginas.length > 1 ? 'telao-coluna-aviso telao-coluna-aviso--ativo' : 'telao-coluna-aviso'}>{aviso}</small>
          <b className="telao-coluna-total">{fase.total}</b>
        </span>
      </header>
      <div className="telao-coluna-corpo" ref={corpoRef}>
        {continuaDe && continuaDe.tipo === 'cabecalho' && (
          <div className="telao-esteira-cabecalho">
            <span className="telao-esteira-cor" style={{ background: continuaDe.cor }} />
            <span className="telao-esteira-nome">{continuaDe.nome} (cont.)</span>
            <span className="telao-esteira-total">{continuaDe.total}</span>
          </div>
        )}
        {itens.map((it, i) => {
          if (!visiveis.has(i)) return null;
          if (it.tipo === 'cabecalho') {
            return (
              <div key={it.chave} data-item={i} data-cabecalho="" className="telao-esteira-cabecalho">
                <span className="telao-esteira-cor" style={{ background: it.cor }} />
                <span className="telao-esteira-nome">{it.nome}</span>
                <span className="telao-esteira-total">{it.total}</span>
              </div>
            );
          }
          if (it.tipo === 'vazio') {
            return (
              <p key={it.chave} data-item={i} className="telao-esteira-vazia">
                Nenhum veículo
              </p>
            );
          }
          return (
            <div key={it.chave} data-item={i} className="telao-placa-casco">
              <PlacaVeiculo veiculo={it.veiculo} onClick={onAbrirVeiculo} />
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ColunaDaFase;
